import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for marketing BLASTS + the email outbox/delivery surface —
 * REP-205 (Listing/Push pillar, Phase 2).
 *
 * A blast is a campaign (subject + bodyHtml + sender + listing). Sending one
 * resolves recipients (on-Repsity vs off-platform — the same split as the
 * Phase-1 push) and ENQUEUES EmailDelivery rows; a cron-driven outbox does
 * the actual SendGrid send. Transactional mail (OTP/tour/reminder/invite)
 * stays on the existing transactional path and is NOT represented here.
 *
 * Endpoints (mounted under `/listings`, FE `/api` stripped upstream):
 *   POST /api/listings/:id/blast                     — create + send a blast
 *   GET  /api/listings/:id/blasts                    — sent-blast list (Outreach tab)
 *   GET  /api/listings/blasts/:blastId/deliveries    — per-recipient delivery rows
 *
 * `:id` = the lister's seller-rep Project id (`listingProjectId`).
 *
 * Keep the types below in sync with `repsity-service/src/dtos/blast.dto.ts`.
 */

/**
 * A single blast recipient. Exactly one of `personId` (on-Repsity, existing
 * Person) or `email` (off-platform, graceful-invite path) should be set; if
 * both are present, the backend lets `personId` win. `name` seeds the
 * placeholder Person on the off-platform invite path. Identical shape to
 * PushRecipient so the composer can reuse its recipient model for
 * "send as a blast".
 */
export interface BlastRecipient {
  personId?: string;
  email?: string;
  name?: string;
}

/**
 * POST body for sending a blast. `subject` + `bodyHtml` are broker-authored
 * marketing content (bodyHtml is rich HTML framed by the LISTING_BLAST
 * template at send time). `listingSheetId` is optional — the backend resolves
 * the active sheet from the seller-rep project when omitted.
 */
export interface SendBlastBody {
  listingSheetId?: string;
  subject: string;
  bodyHtml: string;
  recipients: BlastRecipient[];
}

/** One row per recipient so the FE can reconcile chips after a send. */
export interface BlastEnqueueRow {
  personId?: string | null;
  email?: string | null;
  channel: "BLAST" | "INVITE";
  emailDeliveryId?: string | null;
  inviteId?: string | null;
  suppressed?: boolean;
  skipped?: boolean;
  reason?: string;
}

/**
 * A sent-blast summary row for the Outreach-tab list, each carrying a rollup
 * of its EmailDelivery statuses. `status` / per-recipient statuses are plain
 * strings mirroring the backend EmailDelivery status enum
 * (QUEUED/SENT/DELIVERED/OPENED/CLICKED/BOUNCED/SPAM/UNSUB/FAILED).
 */
export interface BlastSummary {
  id: string;
  subject: string;
  status: string;
  sentAt: string | null;
  createdAt: string;
  recipientCount: number;
  sentCount: number;
  deliveredCount: number;
  openedCount: number;
  clickedCount: number;
  bouncedCount: number;
  unsubscribedCount: number;
}

/** A per-recipient delivery row for the Outreach drawer. */
export interface BlastDeliveryRow {
  id: string;
  toEmail: string;
  personId: string | null;
  status: string;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
}

export const blastApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "blastApi",
    // :id = the lister's seller-rep Project id (listingProjectId).
    baseUrl: "/api/listings",
  }),
  reducerPath: "blastApi",
  tagTypes: ["Blast"],
  endpoints: (builder) => ({
    /**
     * POST /api/listings/:id/blast
     *
     * Create + send a marketing blast. Resolves recipients (on-Repsity vs
     * off-platform) and enqueues EmailDelivery rows; the cron outbox does the
     * actual send. Returns a per-recipient enqueue result. Invalidates the
     * sent-blast list for this listing so the Outreach tab refreshes.
     */
    sendBlast: builder.mutation<
      { data: BlastEnqueueRow[] },
      { listingProjectId: string; body: SendBlastBody }
    >({
      query: ({ listingProjectId, body }) => ({
        url: `/${listingProjectId}/blast`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { listingProjectId }) => [
        { type: "Blast", id: listingProjectId },
      ],
    }),

    /**
     * GET /api/listings/:id/blasts
     *
     * Sent-blast list for the Outreach tab, each with a delivery-status
     * rollup. Keyed/tagged by listingProjectId.
     */
    getBlasts: builder.query<{ data: BlastSummary[] }, string>({
      query: (listingProjectId) => `/${listingProjectId}/blasts`,
      providesTags: (_result, _error, id) => [{ type: "Blast", id }],
    }),

    /**
     * GET /api/listings/blasts/:blastId/deliveries
     *
     * Per-recipient delivery rows for a single blast (the Outreach drawer):
     * delivered / opened / clicked / bounced / unsubscribed, derived from
     * EmailDelivery via the SendGrid event webhook.
     */
    getBlastDeliveries: builder.query<{ data: BlastDeliveryRow[] }, string>({
      query: (blastId) => `/blasts/${blastId}/deliveries`,
      providesTags: (_result, _error, blastId) => [
        { type: "Blast", id: blastId },
      ],
    }),
  }),
});

export const {
  useSendBlastMutation,
  useGetBlastsQuery,
  useGetBlastDeliveriesQuery,
} = blastApi;
